import React, { useCallback, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import debounce from 'lodash/debounce'
import useQueryParams from 'hooks/useQueryParams'
import useCruiseSearchFacets from 'hooks/Cruise/useCruiseSearchFacets'
import { semiFormToObject } from 'lib/forms/formUtils'
import styled from 'styled-components'
import useToggle from 'hooks/useToggle'
import Group from 'components/utils/Group'
import type { FilterPanelCheckItem } from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import FilterPanelCheckboxGroup from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import AssistChip from 'LuxKit/Chips/AssistChip'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import LineTagIcon from 'LuxKit/Icons/line/LineTagIcon'
import config from 'constants/config'
import { LUXURY_ESCAPES } from 'constants/brands'
import { rem } from 'polished'

const CheckboxContainer = styled.div`
  padding-inline: ${rem(16)};
`

const isLuxuryEscapes = config.BRAND === LUXURY_ESCAPES

function buildPanelCheckItems(
  facets: Array<App.CruiseSearchFacet>,
  searchString?: string,
) {
  const items = facets.filter(facet => facet.count)

  if (items.length === 0) return []

  return items.map((item): FilterPanelCheckItem => ({
    value: item.name,
    hidden: !!(searchString && facets.length > 5 && !item.name.toLowerCase().includes(searchString)),
    label: (
      <Group direction="horizontal" horizontalAlign="space-between">
        {item.name}
        <span>({item.count})</span>
      </Group>
    ),
  }))
}

const PromotionsFilterDropdown = styled.div`
  position: relative;
`

interface Props {
  listFilters: App.OfferListFilters
  onChange: (values: Record<string, any>) => void
}

function CruisePromotionsInputFilter(props: Props) {
  const { onChange, listFilters } = props
  const intl = useIntl()
  const [open, toggleSortOpen, _, closeSort] = useToggle()
  const triggerRef = useRef<HTMLButtonElement>(null)

  const queryParams = useQueryParams()
  const containerRef = useRef<HTMLDivElement>(null)

  const facetParams = useMemo(() => ({
    facetTypes: ['special_offers', 'lux_exclusive'],
    cruiseLines: listFilters.cruiseLines,
    departurePlaceId: listFilters.departurePlaceId,
    departureIds: listFilters.departureIds,
    rateCodes: listFilters.rateCodes,
    destinationId: listFilters.destinationId,
    destinationIds: listFilters.destinationIds,
    departureMonths: listFilters.departureMonths,
  } as App.CruiseSearchFacetParams), [listFilters])

  const [facets, fetchingFacets] = useCruiseSearchFacets(facetParams)

  const filters = useMemo(() => {
    const luxExclusiveValue = queryParams.get('isLuxExclusivePromotion')

    return {
      isSpecialOffers: queryParams.get('isSpecialOffers') ? new Set(['Special offers']) : undefined,
      isLuxExclusivePromotion: luxExclusiveValue && isLuxuryEscapes ? new Set(['Lux Exclusive']) : undefined,
    }
  }, [queryParams])

  const leExclusiveItems = useMemo(() => {
    const luxExclusiveFacets = facets.filter(facet => facet.category === 'lux_exclusive')
    return buildPanelCheckItems(luxExclusiveFacets)
  }, [facets])

  const specialOffersItems = useMemo(() => {
    const specialOffersFacets = facets.filter(facet => facet.category === 'special_offers')
    return buildPanelCheckItems(specialOffersFacets)
  }, [facets])

  const onFilterChange = useCallback(() => {
    const formValues = semiFormToObject<Record<string, any>>(containerRef.current!)

    if (formValues.isLuxExclusivePromotion) {
      formValues.isLuxExclusivePromotion = true
    }

    if (formValues.isSpecialOffers) {
      formValues.isSpecialOffers = true
    }

    onChange(formValues)
  }, [onChange])

  const onDelayedFilterChange = useMemo(() => debounce(onFilterChange, 500), [onFilterChange])

  const isVisible = leExclusiveItems.length > 0 || specialOffersItems.length > 0

  return <>
    {isVisible && <PromotionsFilterDropdown ref={containerRef}>
      <AssistChip
        variant="filled"
        size="medium"
        onClick={toggleSortOpen}
        startIcon={<LineTagIcon />}
        endIcon={<DropdownChevron open={open} />}
        ref={triggerRef}
      >
        {intl.formatMessage({
          id: 'IGxc2+sLoB',
          defaultMessage: 'Promotions',
          description: 'Label for cruise promotions filter dropdown chip',
        })}
      </AssistChip>
      <DropdownList
        size="M"
        triggerRef={triggerRef}
        anchorRef={triggerRef}
        open={open}
        onClose={closeSort}
        placement="bottom-end"
      >
        <CheckboxContainer>
          {!fetchingFacets && <>
            <FilterPanelCheckboxGroup
              name="isLuxExclusivePromotion"
              items={leExclusiveItems}
              onChange={onDelayedFilterChange}
              defaultSelected={filters.isLuxExclusivePromotion}
              maxItems={5}
            />

            <FilterPanelCheckboxGroup
              name="isSpecialOffers"
              items={specialOffersItems}
              onChange={onDelayedFilterChange}
              defaultSelected={filters.isSpecialOffers}
              maxItems={5}
            />
          </>}
        </CheckboxContainer>
      </DropdownList>
    </PromotionsFilterDropdown>}
  </>
}

export default CruisePromotionsInputFilter
